import { DialogComponent } from '@theme/dialog';
import { CartAddEvent, CartUpdateEvent } from '@theme/events';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);

    document.addEventListener('gcp-item-added', async () => {
      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sectionIds = /** @type {string[]} */ ([...cartItemsComponents]
        .map((item) => /** @type {HTMLElement} */ (item).dataset.sectionId)
        .filter(Boolean));

      const [cart, ...sectionHTMLs] = await Promise.all([
        fetch(`${Theme.routes.cart_url}.js`).then((r) => r.json()),
        ...sectionIds.map((id) => sectionRenderer.getSectionHTML(id, false)),
      ]);

      /** @type {Record<string, string>} */
      const sections = {};
      sectionIds.forEach((id, index) => {
        if (sectionHTMLs[index]) sections[id] = sectionHTMLs[index];
      });

      document.dispatchEvent(
        new CartUpdateEvent({}, '', {
          itemCount: cart.item_count,
          source: 'cart-drawer',
          sections,
        })
      );

      this.open();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = (event) => {
    if (event.detail.data?.silent) return;

    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
